import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, User, Users, CalendarDays, Briefcase, Building2, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Avatar } from '@/components/ui/avatar';
import { searchAll, resultHref } from '@/lib/api/search';
import type { SearchResult } from '@/lib/api/search';
import { cn } from '@/lib/utils/cn';
import { useDebouncedValue } from '@/lib/hooks/useDebouncedValue';

const ENTITY_ICON: Record<string, React.ElementType> = {
  profile: User,
  group: Users,
  event: CalendarDays,
  job: Briefcase,
  venture: Building2,
};

const ENTITY_LABEL: Record<string, string> = {
  profile: 'Profile',
  group: 'Group',
  event: 'Event',
  job: 'Job',
  venture: 'Venture',
};

type Props = {
  open: boolean;
  onClose: () => void;
};

export function CommandPalette({ open, onClose }: Props) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [raw, setRaw] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const q = useDebouncedValue(raw, 200);

  const { data: results = [] } = useQuery({
    queryKey: ['cmd-search', q],
    queryFn: () => searchAll(q, 8),
    enabled: q.trim().length >= 2,
    staleTime: 15_000,
  });

  // Focus input when opened.
  useEffect(() => {
    if (open) {
      setRaw('');
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Reset active index when results change.
  useEffect(() => { setActiveIdx(0); }, [results]);

  if (!open) return null;

  function commit(r: SearchResult) {
    navigate(resultHref(r));
    onClose();
  }

  function handleKey(e: React.KeyboardEvent) {
    if (e.key === 'Escape') { onClose(); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results[activeIdx]) {
        commit(results[activeIdx]);
      } else if (raw.trim()) {
        navigate(`/search?q=${encodeURIComponent(raw.trim())}`);
        onClose();
      }
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-ink/30 backdrop-blur-sm px-4 pt-[10vh] md:pt-[15vh]"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      aria-modal="true"
      role="dialog"
      aria-label="Search"
    >
      <div className="w-full max-w-xl rounded-2xl border border-border/70 bg-white shadow-2xl">
        {/* Input row */}
        <div className="flex items-center gap-3 border-b border-border/60 px-4 py-3">
          <Search className="h-4 w-4 shrink-0 text-muted" aria-hidden="true" />
          <Input
            ref={inputRef}
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Search people, groups, events, jobs, ventures…"
            className="h-auto border-0 bg-transparent px-0 py-0 text-base shadow-none focus-visible:ring-0"
            aria-label="Search"
            aria-autocomplete="list"
            aria-activedescendant={results[activeIdx] ? `cmd-result-${activeIdx}` : undefined}
          />
          {raw && (
            <button
              type="button"
              onClick={() => setRaw('')}
              className="shrink-0 text-muted hover:text-ink"
              aria-label="Clear"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Results */}
        {results.length > 0 && (
          <ul role="listbox" className="max-h-80 overflow-y-auto py-2">
            {results.map((r, i) => {
              const Icon = ENTITY_ICON[r.entity_type] ?? Search;
              return (
                <li key={r.id} id={`cmd-result-${i}`} role="option" aria-selected={i === activeIdx}>
                  <button
                    type="button"
                    onMouseEnter={() => setActiveIdx(i)}
                    onClick={() => commit(r)}
                    className={cn(
                      'flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors',
                      i === activeIdx ? 'bg-cream' : 'hover:bg-cream/60',
                    )}
                  >
                    {r.image_url ? (
                      <Avatar src={r.image_url} name={r.title} size="sm" />
                    ) : (
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-navy/8">
                        <Icon className="h-4 w-4 text-navy/60" aria-hidden="true" />
                      </span>
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-navy">{r.title}</span>
                      {r.subtitle && (
                        <span className="block truncate text-xs text-muted">{r.subtitle}</span>
                      )}
                    </span>
                    <span className="shrink-0 rounded-full bg-border/60 px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted">
                      {ENTITY_LABEL[r.entity_type] ?? r.entity_type}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {/* Empty state */}
        {q.trim().length >= 2 && results.length === 0 && (
          <p className="px-4 py-6 text-center text-sm text-muted">No results for "{q}"</p>
        )}

        {/* Footer hint */}
        <div className="flex items-center justify-between border-t border-border/60 px-4 py-2 text-[11px] text-muted">
          <span>↑↓ navigate · Enter select · Esc close</span>
          {raw.trim().length >= 2 && (
            <button
              type="button"
              onClick={() => { navigate(`/search?q=${encodeURIComponent(raw.trim())}`); onClose(); }}
              className="text-gold hover:underline"
            >
              See all results →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
